import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../api/client';
import { Loader2, Check, FolderTree, Search, X } from 'lucide-react';
import { Can } from '../../../lib/rbac';

/**
 * Revisión de categoría POR LOTES: agrupa los borradores en revisión por
 * (marca + categoría propuesta). Un clic aprueba el grupo entero con la
 * propuesta, o se elige otra categoría para todos. Cada resolución queda
 * además como enseñanza en la memoria de categorización.
 */

interface GroupItem {
  queue_id: number;
  asin: string;
  title: string | null;
  thumbnail: string | null;
}

interface Group {
  key: string;
  brand: string;
  proposed_category_id: string | null;
  proposed_category_path: string | null;
  count: number;
  items: GroupItem[];
}

export function ReviewGroupsView({ sellerId, onResolved }: { sellerId: number; onResolved: () => void }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [pickerGroup, setPickerGroup] = useState<Group | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSuggestions, setPickerSuggestions] = useState<any[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [message, setMessage] = useState('');

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.get<any>(`/publisher/drafts/review-groups?seller_id=${sellerId}`);
      if (res.success) setGroups(res.groups || []);
    } catch (err) {
      console.error('review-groups fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const resolve = async (group: Group, categoryId: string, categoryName: string | null) => {
    setResolving(group.key);
    setMessage('');
    try {
      const res: any = await api.post<any>('/publisher/drafts/review-groups/resolve', {
        seller_id: sellerId,
        queue_ids: group.items.map(i => i.queue_id),
        category_id: categoryId,
        category_name: categoryName,
      });
      if (res.success) {
        const pub = res.auto_published || 0;
        setMessage(
          `✓ ${res.resolved} productos resueltos a "${categoryName || categoryId}"` +
          (pub > 0 ? ` — ${pub} enviados a publicar automáticamente` : '')
        );
        setPickerGroup(null);
        await fetchGroups();
        onResolved();
      } else {
        setMessage(`Error: ${res.error || 'no se pudo resolver'}`);
      }
    } catch (err: any) {
      setMessage(`Error: ${err?.message || 'no se pudo resolver'}`);
    } finally {
      setResolving(null);
    }
  };

  const searchCategories = async () => {
    if (!pickerSearch.trim()) return;
    setPickerLoading(true);
    try {
      const res = await api.post<any>('/publisher/manual/categorize', {
        title: pickerSearch,
        category_path: pickerSearch,
        seller_id: sellerId,
      });
      // Forma real de /manual/categorize (categorizeGroups): data.groups[] con
      // suggested {id,name,path,confidence} + alternatives [{id,name,path}].
      // El parseo anterior buscaba matches/category_id (campos inexistentes) →
      // la lista quedaba siempre vacía y el popup parecía roto.
      const suggestions: any[] = [];
      for (const g of res?.data?.groups || []) {
        if (g.suggested) suggestions.push(g.suggested);
        for (const alt of g.alternatives || []) suggestions.push(alt);
      }
      setPickerSuggestions(
        suggestions.map((s: any) => ({
          category_id: s.id,
          path: s.path,
          name: s.name,
          confidence: s.confidence,
        }))
      );
    } catch (err) {
      console.error('categorize failed:', err);
    } finally {
      setPickerLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12 text-[var(--color-text-secondary)]">
        <Loader2 size={20} className="animate-spin mr-2" /> Cargando grupos…
      </div>
    );

  if (!groups.length)
    return (
      <div className="text-center py-12 text-[var(--color-text-secondary)]">
        <FolderTree size={32} className="mx-auto mb-2 opacity-50" />
        No hay borradores en revisión por categoría. 🎉
      </div>
    );

  return (
    <div className="space-y-3">
      {message && (
        <div className="px-3 py-2 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]">
          {message}
        </div>
      )}
      {groups.map((g) => (
        <div
          key={g.key}
          className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] space-y-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--color-text)]">
                {g.brand} <span className="text-[var(--color-text-tertiary)]">· {g.count} producto{g.count > 1 ? 's' : ''}</span>
              </div>
              <div className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                Propuesta: <span className="font-medium">{g.proposed_category_path || '(sin propuesta)'}</span>
                {g.proposed_category_id && (
                  <span className="font-mono text-[10px] ml-1 text-[var(--color-text-tertiary)]">{g.proposed_category_id}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Can cap="publisher.drafts_review">
              {g.proposed_category_id && (
                <button
                  onClick={() => resolve(g, g.proposed_category_id!, g.proposed_category_path)}
                  disabled={resolving === g.key}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
                >
                  {resolving === g.key ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Aprobar propuesta para todos
                </button>
              )}
              <button
                onClick={() => { setPickerGroup(g); setPickerSearch(''); setPickerSuggestions([]); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-bg-tertiary)] hover:bg-[var(--color-border)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg text-xs font-medium transition-colors"
              >
                <FolderTree size={12} /> Elegir otra categoría…
              </button>
              </Can>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {g.items.slice(0, 8).map((it) => (
              <div key={it.queue_id} className="flex items-center gap-2 px-2 py-1 rounded bg-[var(--color-bg)] border border-[var(--color-border-light)] max-w-[260px]" title={it.title || it.asin}>
                {it.thumbnail && <img src={it.thumbnail} alt="" className="w-7 h-7 object-contain rounded" />}
                <span className="text-[11px] text-[var(--color-text-secondary)] truncate">{it.title || it.asin}</span>
              </div>
            ))}
            {g.items.length > 8 && (
              <span className="text-[11px] text-[var(--color-text-tertiary)] self-center">+{g.items.length - 8} más</span>
            )}
          </div>
        </div>
      ))}

      {/* Selector de otra categoría para el grupo */}
      {pickerGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPickerGroup(null)}>
          <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] shadow-xl w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-[var(--color-text)]">
                Categoría para {pickerGroup.count} producto{pickerGroup.count > 1 ? 's' : ''} de {pickerGroup.brand}
              </h3>
              <button onClick={() => setPickerGroup(null)} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]"><X size={16} /></button>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
                <input
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchCategories()}
                  placeholder="Buscar categoría (ej: perfumes, libros…)"
                  className="w-full pl-8 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
                />
              </div>
              <button onClick={searchCategories} disabled={pickerLoading} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium">
                {pickerLoading ? <Loader2 size={14} className="animate-spin" /> : 'Buscar'}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {pickerSuggestions.map((s: any, i: number) => (
                <button
                  key={`${s.category_id}-${i}`}
                  onClick={() => resolve(pickerGroup, s.category_id, s.path || s.category_name || s.name || null)}
                  disabled={resolving === pickerGroup.key}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] text-xs text-[var(--color-text)] border border-transparent hover:border-[var(--color-border)] transition-colors"
                >
                  <span className="font-mono text-[10px] text-[var(--color-text-tertiary)] mr-1.5">{s.category_id}</span>
                  {s.path || s.category_name || s.name}
                </button>
              ))}
              {!pickerSuggestions.length && !pickerLoading && (
                <div className="text-xs text-[var(--color-text-tertiary)] text-center py-4">Buscá para ver opciones</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
